import React, { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { useCode } from "./use-code";
import { syntaxStyle } from "./syntaxStyle";
import CodeCopyButton from "~/components/code/CodeCopyButton";
import { useCopyCodeButtonScroll } from "./hooks/useCopyCodeButtonScroll";

type CodeProps = {
  languages?: string[];
  children: ReactNode;
  copyButton?: boolean;
  codeStyle?: CSSProperties;
  dataId?: string;
  onCopy?: () => void;
  ContainerProps?: {
    sx?: SxProps<Theme>;
  };
} & HTMLAttributes<HTMLPreElement>;

// This implementation is based on mit licensed https://github.com/akiran/react-highlight
export function Code({
  className,
  children = "",
  copyButton,
  codeStyle,
  dataId,
  onCopy,
  languages = [],
  ...props
}: CodeProps) {
  const { elRef, justCopied, handleCopyToClipboard } = useCode({
    languages,
    className,
  });

  const { isHidden, hideButton, showButton } = useCopyCodeButtonScroll(
    elRef as React.RefObject<HTMLPreElement | null>,
  );

  const containerSx = props.ContainerProps?.sx
    ? Array.isArray(props.ContainerProps.sx)
      ? Object.assign({}, ...props.ContainerProps.sx) // Flatten into a single object
      : props.ContainerProps.sx
    : {};

  return (
    <Box
      data-testid="code"
      sx={[
        {
          position: "relative",
          my: 2,
          "& code": {
            backgroundColor: "unset",
            border: "unset",
            padding: "unset",
            minHeight: (theme) => theme.spacing(7.25),
          },
          ...containerSx,
          ...syntaxStyle(),
        },
      ]}
    >
      {copyButton && children && (
        <CodeCopyButton
          {...{
            dataId,
            justCopied,
            onClick: (e) => {
              handleCopyToClipboard(e);
              onCopy?.();
            },
            isVisible: !isHidden,
            onMouseEnter: showButton,
          }}
        />
      )}
      <pre
        ref={elRef}
        onScroll={hideButton}
        onWheel={hideButton}
        style={{ margin: 0, ...props.style }}
      >
        <code
          className={className}
          contentEditable={props.contentEditable}
          onBlur={props.onBlur}
          onFocus={props.onFocus}
          style={codeStyle}
          suppressContentEditableWarning
        >
          {children}
        </code>
      </pre>
    </Box>
  );
}
